import useCreateLinkedEasyPVProject from '@/api/mutations/project/useCreateLinkedEasyPVProject';
import useCreateLinkedOpenSolarProject from '@/api/mutations/project/useCreateLinkedOpenSolarProject';
import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Conditional,
  Field,
  Modal,
  Select,
  useToastContext,
  Text,
} from '@payaca/ambrosia';

import useCreateLinkedHeatpunkProject from '@/api/mutations/project/useCreateLinkedHeatpunkProject';
import useGetMyAccountIntegrations from '@/api/queries/me/useGetMyAccountIntegrations';
import projectKeys from '@/api/queries/project/keyFactory';
import useGetCanBeLinkedToSpruce from '@/api/queries/project/useGetCanBeLinkedToSpruce';
import useGetProjectIntegrations from '@/api/queries/project/useGetProjectIntegrations';
import {
  CreateLinkedEasyPvProjectMutation,
  CreateLinkedHeatpunkProjectMutation,
  EasyPvDesignMode,
} from '@/gql/graphql';
import { readableProjectIntegrationService } from '@payaca/helpers/projectIntegrationsHelper';
import { ProjectIntegrationService } from '@payaca/types/projectIntegrationTypes';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

type CreateLinkedProjectModalProps = CreateLinkedProjectModalContentProps & {
  isOpen: boolean;
};
const CreateLinkedProjectModal: FC<CreateLinkedProjectModalProps> = (props) => {
  const { isOpen, projectId, onClose, onSelectSpruce } = props;
  return (
    <Modal title="Create linked project" isOpen={isOpen} onClose={onClose}>
      {isOpen && (
        <CreateLinkedProjectModalContent
          projectId={projectId}
          onClose={onClose}
          onSelectSpruce={onSelectSpruce}
        />
      )}
    </Modal>
  );
};

type TBaseCreateProjectFormState = {
  projectId: string;
};
type TCreateEasyPVProjectFormState = {
  designMode: EasyPvDesignMode;
  linkedProjectOwnerId: string;
  service: 'EASY_PV';
} & TBaseCreateProjectFormState;
type TCreateHeatpunkProjectFormState = {
  linkedProjectOwnerId: string;
  service: 'HEATPUNK';
} & TBaseCreateProjectFormState;
type TCreateOpenSolarProjectFormState = {
  service: 'OPEN_SOLAR';
} & TBaseCreateProjectFormState;
type TCreateSpruceProjectFormState = {
  service: 'SPRUCE';
} & TBaseCreateProjectFormState;
type TFormState =
  | TCreateOpenSolarProjectFormState
  | TCreateEasyPVProjectFormState
  | TCreateHeatpunkProjectFormState
  | TCreateSpruceProjectFormState;
const BASE_CREATE_LINKED_PROJECT_SCHEMA = z.object({
  projectId: z.string(),
});
const CREATE_LINKED_EASY_PV_PROJECT_SCHEMA =
  BASE_CREATE_LINKED_PROJECT_SCHEMA.extend({
    designMode: z.enum([
      'MAGIC',
      'QUICK_ROOF',
      'ROOF_OUTLINE',
      'THREE_D_MODEL',
    ]),
    service: z.literal('EASY_PV'),
    linkedProjectOwnerId: z.string(),
  });
const CREATE_LINKED_HEATPUNK_PROJECT_SCHEMA =
  BASE_CREATE_LINKED_PROJECT_SCHEMA.extend({
    service: z.literal('HEATPUNK'),
    linkedProjectOwnerId: z.string(),
  });
const CREATE_LINKED_OPEN_SOLAR_PROJECT_SCHEMA =
  BASE_CREATE_LINKED_PROJECT_SCHEMA.extend({
    service: z.literal('OPEN_SOLAR'),
  });
const CREATE_LINKED_SPRUCE_PROJECT_SCHEMA =
  BASE_CREATE_LINKED_PROJECT_SCHEMA.extend({
    service: z.literal('SPRUCE'),
  });
const CREATE_LINKED_PROJECT_SCHEMA = z.discriminatedUnion('service', [
  CREATE_LINKED_EASY_PV_PROJECT_SCHEMA,
  CREATE_LINKED_HEATPUNK_PROJECT_SCHEMA,
  CREATE_LINKED_OPEN_SOLAR_PROJECT_SCHEMA,
  CREATE_LINKED_SPRUCE_PROJECT_SCHEMA,
]);
const easyPVDesignModeOptions: { label: string; value: EasyPvDesignMode }[] = [
  { label: 'Magic', value: 'MAGIC' },
  { label: 'Quick roof', value: 'QUICK_ROOF' },
  { label: 'Roof outline', value: 'ROOF_OUTLINE' },
  { label: '3D', value: 'THREE_D_MODEL' },
];
type CreateLinkedProjectModalContentProps = {
  projectId: string;
  onClose: () => void;
  onSelectSpruce: () => void;
};
const CreateLinkedProjectModalContent: FC<
  CreateLinkedProjectModalContentProps
> = (props) => {
  const { projectId, onClose, onSelectSpruce } = props;
  const { pushSuccess } = useToastContext();
  const queryClient = useQueryClient();
  const { data: myAccountIntegrations } = useGetMyAccountIntegrations();
  const { data: accountUsers } = useGetMyAccountUsersExcludedDeactivated();
  const { integrations: projectIntegrations } =
    useGetProjectIntegrations(projectId);

  const hasSpruceConnection = myAccountIntegrations.some(
    (i) => i.service === 'SPRUCE'
  );

  const { spruceAvailability } = useGetCanBeLinkedToSpruce(projectId, {
    enabled: hasSpruceConnection,
  });

  const getSpruceOptionState = (): {
    disabled: boolean;
    reasons?: string[];
  } => {
    if (!spruceAvailability) return { disabled: true };
    if (spruceAvailability.result) return { disabled: false };

    const nonPropertyReasons = spruceAvailability.reasons.filter(
      (r) => r.code !== 'MISSING_PROPERTY_INFO'
    );
    if (nonPropertyReasons.length === 0) return { disabled: false };

    return {
      disabled: true,
      reasons: nonPropertyReasons.map((r) => r.message),
    };
  };

  type ServiceOptionMetadata = { blockerReasons?: string[] };

  const serviceOptions: {
    label: string;
    value: ProjectIntegrationService;
    disabled?: boolean;
    metadata?: ServiceOptionMetadata;
  }[] = myAccountIntegrations
    ?.filter(
      (accountIntegration) =>
        // only allow one integration per service
        !projectIntegrations?.find(
          (pi) => pi.service === accountIntegration.service
        )
    )
    .map((accountIntegration) => {
      if (accountIntegration.service === 'SPRUCE') {
        const state = getSpruceOptionState();
        return {
          label: readableProjectIntegrationService[accountIntegration.service],
          value: accountIntegration.service,
          disabled: state.disabled,
          metadata: state.reasons?.length
            ? { blockerReasons: state.reasons }
            : undefined,
        };
      }
      return {
        label: readableProjectIntegrationService[accountIntegration.service],
        value: accountIntegration.service,
      };
    });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    mutateAsync: createLinkedEasyPVProject,
    isPending: isCreatingEasyPVProject,
  } = useCreateLinkedEasyPVProject({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.integrations(projectId),
      });
    },
  });
  const {
    mutateAsync: createLinkedHeatpunkProject,
    isPending: isCreatingHeatpunkProject,
  } = useCreateLinkedHeatpunkProject({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.integrations(projectId),
      });
    },
  });

  const {
    mutateAsync: createLinkedOpenSolarProject,
    isPending: isCreatingOpenSolarProject,
  } = useCreateLinkedOpenSolarProject({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.integrations(projectId),
      });
    },
  });
  const formMethods = useForm<TFormState>({
    resolver: zodResolver(CREATE_LINKED_PROJECT_SCHEMA),
    defaultValues: {
      projectId,
    },
  });

  const isCreatingLinkedProject =
    isCreatingEasyPVProject ||
    isCreatingHeatpunkProject ||
    isCreatingOpenSolarProject;
  const selectedService = formMethods.watch('service');
  const midsummerProjectOwnerOptions = accountUsers
    .filter((u) => !!u.email)
    .map((user) => ({
      label: `${user.fullName} - ${user.email}`,
      value: user.publicId,
    }));

  const handleCreateLinkedProject = async (data: TFormState) => {
    if (data.service === 'SPRUCE') {
      onSelectSpruce();
      return;
    }

    let result:
      | CreateLinkedEasyPvProjectMutation['createLinkedEasyPVProject']
      | CreateLinkedHeatpunkProjectMutation['createLinkedHeatpunkProject']
      | null = null;

    switch (data.service) {
      case 'EASY_PV': {
        const response = await createLinkedEasyPVProject({
          projectId,
          designMode: data.designMode,
          linkedProjectOwnerId: data.linkedProjectOwnerId,
        });
        result = response.createLinkedEasyPVProject;
        break;
      }
      case 'HEATPUNK': {
        const response = await createLinkedHeatpunkProject({
          projectId,
          linkedProjectOwnerId: data.linkedProjectOwnerId,
        });
        result = response.createLinkedHeatpunkProject;
        break;
      }
      case 'OPEN_SOLAR': {
        const response = await createLinkedOpenSolarProject({
          projectId,
        });
        result = response.createLinkedOpenSolarProject;
        break;
      }
      default:
        setErrorMessage('Sorry, this integration is not supported yet.');
        return;
    }

    if (result) {
      if (result.__typename === 'CreateLinkedProjectError') {
        setErrorMessage(result.message);
      } else if (result.__typename === 'CreateLinkedProjectSuccess') {
        pushSuccess(
          `${
            readableProjectIntegrationService[data.service]
          } project created successfully`
        );
        onClose();
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          handleCreateLinkedProject,
          console.error
        )}
      >
        <>
          <Modal.Body>
            <Controller
              name="service"
              control={formMethods.control}
              render={({ field: { name, ...rest } }) => {
                return (
                  <Field className="mb-4" name={name}>
                    <Field.Label>Integration</Field.Label>
                    <Select
                      multiple={false}
                      options={serviceOptions}
                      CustomOption={({ option }) => (
                        <div>
                          <Text as="span">{option.label}</Text>
                          {option.metadata?.blockerReasons && (
                            <Text as="div" level="supporting" colour="primary">
                              <ul className="mt-1 list-disc pl-4">
                                {option.metadata.blockerReasons.map(
                                  (reason, idx) => (
                                    <li key={idx}>{reason}</li>
                                  )
                                )}
                              </ul>
                            </Text>
                          )}
                        </div>
                      )}
                      {...rest}
                    />
                  </Field>
                );
              }}
            />
            <Conditional
              condition={['EASY_PV', 'HEATPUNK'].includes(selectedService)}
            >
              <Controller
                name="linkedProjectOwnerId"
                control={formMethods.control}
                render={({ field: { name, ...rest } }) => {
                  return (
                    <Field className="mb-4" name={name}>
                      <Field.Label>Project owner</Field.Label>
                      <Field.HelperText>
                        This user email must have{' '}
                        {readableProjectIntegrationService[selectedService]}{' '}
                        access
                      </Field.HelperText>
                      <Select
                        multiple={false}
                        options={midsummerProjectOwnerOptions}
                        {...rest}
                      />
                    </Field>
                  );
                }}
              />
            </Conditional>
            <Conditional condition={selectedService === 'EASY_PV'}>
              <Controller
                name="designMode"
                control={formMethods.control}
                render={({ field: { name, ...rest } }) => {
                  return (
                    <Field className="mb-4" name={name}>
                      <Field.Label>Design mode</Field.Label>
                      <Select
                        multiple={false}
                        options={easyPVDesignModeOptions}
                        {...rest}
                      />
                    </Field>
                  );
                }}
              />
            </Conditional>

            {errorMessage ? (
              <Alert colour="red" className="mt-2">
                {errorMessage}
              </Alert>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                type="submit"
                isProcessing={isCreatingLinkedProject}
                disabled={isCreatingLinkedProject}
              >
                Create
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      </form>
    </FormProvider>
  );
};

export default CreateLinkedProjectModal;
